/* =====================================================================
 * @auw/tenant — On-premise resolver helpers (Phase 8).
 *
 * Turn the opaque analytics / payments / asset-font slices of a
 * `TenantManifest` into concrete booleans and URLs the loaders at
 * `askyourai/src/lib/*` and `askyourai/src/monitoring/*` can consume
 * without having to know the manifest shape.
 *
 * Design notes
 *   - Every resolver is a *pure function* of the manifest. Consumers
 *     that also need the env-var fallback (e.g. `VITE_GA_MEASUREMENT_ID`)
 *     layer that in themselves — the tenant layer stays free of
 *     `import.meta.env` so the package remains trivially testable.
 *   - Missing provider sections collapse to *disabled*. This mirrors
 *     the Phase 4/Phase 8 agreement that on-prem tenants MUST opt into
 *     any external network hit.
 *   - `resolveTenantAssetFonts` keeps backwards compatibility with the
 *     pre-Phase-8 hardcoded defaults via the `fallback` argument so
 *     `runtimeFontLoader.ts` can replay the legacy B2C URLs when a
 *     tenant manifest simply omits the `assets.fonts` block.
 * ===================================================================== */

import type {
  TenantAnalyticsGa4,
  TenantAnalyticsSentry,
  TenantAssetFonts,
  TenantManifest,
  TenantPaymentsPaddle,
} from './types';

export interface ResolvedFontSources {
  readonly bodyHref?: string;
  readonly monospaceHref?: string;
  readonly externalRegistryEnabled: boolean;
}

/**
 * Returns the tenant-specified GA4 configuration or `null` when GA4
 * is disabled. Missing `analytics.ga4` is treated as disabled so
 * on-prem tenants only need to omit the field.
 */
export function resolveTenantAnalyticsGa4(
  tenant: TenantManifest,
): TenantAnalyticsGa4 | null {
  const ga4 = tenant.analytics?.ga4;
  if (!ga4) return null;
  if (ga4.enabled !== true) return null;
  return ga4;
}

/**
 * Returns the tenant-specified Sentry configuration or `null` when
 * Sentry is disabled. Missing `analytics.sentry` is treated as
 * disabled.
 */
export function resolveTenantAnalyticsSentry(
  tenant: TenantManifest,
): TenantAnalyticsSentry | null {
  const sentry = tenant.analytics?.sentry;
  if (!sentry) return null;
  if (sentry.enabled !== true) return null;
  return sentry;
}

/**
 * Returns the tenant-specified Paddle configuration or `null` when
 * payments are disabled. Per the Phase 4 agreement on-prem tenants
 * MUST NOT opt in.
 */
export function resolveTenantPaymentsPaddle(
  tenant: TenantManifest,
): TenantPaymentsPaddle | null {
  const paddle = tenant.payments?.paddle;
  if (!paddle) return null;
  if (paddle.enabled !== true) return null;
  return paddle;
}

/**
 * Resolve the UI font URLs and the doc-agent external registry gate.
 *
 * `fallback` supplies the B2C hardcoded defaults so that a missing
 * `assets.fonts` block keeps exact parity with the pre-Phase-8
 * `runtimeFontLoader.ts`. Empty-string `bodyHref` / `monospaceHref`
 * explicitly disables the slot (on-prem self-host).
 */
export function resolveTenantAssetFonts(
  tenant: TenantManifest,
  fallback: Pick<TenantAssetFonts, 'bodyHref' | 'monospaceHref'> & {
    readonly externalRegistryEnabled?: boolean;
  } = {},
): ResolvedFontSources {
  const fonts = tenant.assets.fonts;
  const bodyRaw = fonts?.bodyHref;
  const monoRaw = fonts?.monospaceHref;
  return {
    bodyHref:
      typeof bodyRaw === 'string'
        ? (bodyRaw.trim().length > 0 ? bodyRaw : undefined)
        : fallback.bodyHref,
    monospaceHref:
      typeof monoRaw === 'string'
        ? (monoRaw.trim().length > 0 ? monoRaw : undefined)
        : fallback.monospaceHref,
    externalRegistryEnabled:
      typeof fonts?.externalRegistryEnabled === 'boolean'
        ? fonts.externalRegistryEnabled
        : fallback.externalRegistryEnabled ?? true,
  };
}

/**
 * Resolve the pdf.js worker URL. Returns `undefined` when neither the
 * tenant nor the caller supplies an override; `PdfRenderer.tsx` keeps
 * its legacy unpkg default in that case.
 */
export function resolveTenantPdfWorkerUrl(
  tenant: TenantManifest,
  fallback?: string,
): string | undefined {
  const override = tenant.assets.pdfWorkerUrl;
  if (typeof override === 'string' && override.trim().length > 0) {
    return override;
  }
  return fallback;
}

/**
 * Resolve the pdf.js CMap directory URL. Same fallback contract as
 * `resolveTenantPdfWorkerUrl` — empty string / absent → caller-supplied
 * default. Required for CJK / non-Latin1 PDF rendering; B2C points at
 * unpkg's versioned `cmaps/` folder, on-prem tenants self-host.
 */
export function resolveTenantPdfCMapUrl(
  tenant: TenantManifest,
  fallback?: string,
): string | undefined {
  const override = tenant.assets.pdfCMapUrl;
  if (typeof override === 'string' && override.trim().length > 0) {
    return override;
  }
  return fallback;
}

/**
 * Resolve the pdf.js standard-font directory URL (Foxit `*.pfb` files
 * shipped under `pdfjs-dist/standard_fonts/`). Same fallback contract
 * as the worker resolver. Required when a PDF leaves Base14 fonts
 * unembedded; without it pdfjs falls back to host fonts and glyphs
 * mis-align.
 */
/**
 * pdf.js WASM 디렉터리 URL. 다른 슬롯과 같은 폴백 계약이다.
 *
 * ⚠️ 다른 슬롯과 달리 **없으면 조용히 나빠지지 않는다.** cMap 이 없으면 CJK 가
 * □ 로 보이는 정도지만, 이것이 없으면 JBIG2/JPX 디코더가 초기화에 실패해 예외를
 * 던진다 — 스캔 PDF 페이지가 통째로 안 그려진다.
 */
export function resolveTenantPdfWasmUrl(
  tenant: TenantManifest,
  fallback?: string,
): string | undefined {
  const override = tenant.assets.pdfWasmUrl;
  if (typeof override === 'string' && override.trim().length > 0) {
    return override;
  }
  return fallback;
}

export function resolveTenantPdfStandardFontDataUrl(
  tenant: TenantManifest,
  fallback?: string,
): string | undefined {
  const override = tenant.assets.pdfStandardFontDataUrl;
  if (typeof override === 'string' && override.trim().length > 0) {
    return override;
  }
  return fallback;
}
